'use client';

import { useRef, useState } from 'react';

import Image from 'next/image';

import { motion } from 'framer-motion';
import { ArrowDown, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InteractiveGrid } from '@/components/ui/interactive-grid';
import { useTranslation } from '@/lib/i18n/context';

interface HeroOpenAcademyProps {
  onSearch?: (query: string) => void;
}

const HeroOpenAcademy = ({ onSearch }: HeroOpenAcademyProps) => {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState('');

  const handleSearch = () => {
    onSearch?.(searchValue);
    document.getElementById('courses')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      ref={sectionRef}
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-black pt-32 pb-20 text-white md:pt-40"
    >
      {/* Background - Interactive Grid */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {/* Background Image - Darker for Academy */}
        <div className="absolute inset-0 z-0">
          <Image
            src="/images/academy/academy-hero.webp"
            alt=""
            aria-hidden
            fill
            className="object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        <div className="pointer-events-auto absolute top-0 left-0 z-10 h-full w-full">
          <InteractiveGrid
            width={20}
            height={20}
            lineColor="rgba(255,255,255,0.01)"
            className="opacity-100"
            showHint={false}
            revealImageSrc="/images/academy/academy-hero.webp"
          />
        </div>
        <div className="pointer-events-none absolute inset-0 z-20 bg-gradient-to-b from-black/0 via-black/0 to-black" />
      </div>

      <div className="relative z-10 container">
        {/* Text Content */}
        <div className="mb-8 space-y-8 text-center lg:mb-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="mx-auto max-w-4xl space-y-6"
          >
            <h1 className="font-serif text-5xl text-white sm:text-6xl lg:text-8xl">
              {t.academy['Master AI for Customer Support']}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
              className="mx-auto max-w-2xl text-xl text-neutral-400 lg:text-2xl"
            >
              {
                t.academy[
                  'The best place on earth to learn AI for customer support.'
                ]
              }
            </motion.p>
          </motion.div>
        </div>

        {/* Search Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-0 mx-auto max-w-3xl"
        >
          <div className="group relative rounded-full shadow-[0_0_40px_-10px_rgba(255,255,255,0.1)]">
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 opacity-70 blur-xl transition-all duration-500 group-hover:opacity-100 group-hover:blur-2xl" />
            <div className="relative flex items-center rounded-full bg-white/5 p-2 ring-1 ring-white/10 backdrop-blur-xl transition-all duration-300 hover:bg-white/10 hover:ring-white/20">
              <Search className="ml-4 h-6 w-6 text-neutral-400" />
              <Input
                type="text"
                placeholder={t.academy['Learn something new today!']}
                autoFocus
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="h-14 border-0 bg-transparent px-4 text-lg text-white placeholder:text-neutral-400 focus-visible:ring-0 focus-visible:ring-offset-0 md:text-xl"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
              <Button
                size="lg"
                className="rounded-full bg-white px-8 text-black hover:bg-neutral-200"
                onClick={handleSearch}
              >
                {t.academy.Search}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Scroll Down Button */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          delay: 1,
          duration: 1,
          repeat: Infinity,
          repeatType: 'reverse',
        }}
        className="absolute bottom-8 left-1/2 z-20 -translate-x-1/2"
      >
        <Button
          variant="ghost"
          size="icon"
          className="h-12 w-12 rounded-full border border-white/10 bg-white/5 text-white backdrop-blur-sm transition-all hover:bg-white/10 hover:text-white"
          onClick={() => {
            document
              .getElementById('courses')
              ?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          <ArrowDown className="h-6 w-6" />
        </Button>
      </motion.div>
    </section>
  );
};

export default HeroOpenAcademy;
